import React, { useRef, useState } from 'react';
import { FileUp, Loader2, X } from 'lucide-react';
import { FILE_ACCEPT, FILE_TAG_KEYS, switchCopy } from './switch';

const guessTag = (name) => {
  const n = name.toLowerCase();
  if (/stock|inventor|part|item/.test(n)) return 'stock';
  if (/due|balance|outstanding|receivabl|ledger/.test(n)) return 'balances';
  if (/vehicle|car/.test(n)) return 'vehicles';
  if (/customer|client|party|contact/.test(n)) return 'customers';
  return 'other';
};

// Pick files and tag each one; the parent uploads them.
export const FilePicker = ({ files, setFiles, busy, progress, t = switchCopy('en').form }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  // Copy the FileList now: it is live and gets emptied when the input value is reset below.
  const add = (list) => {
    const picked = [...list].map((file) => ({ file, tag: guessTag(file.name), id: `${file.name}-${file.size}-${Math.random()}` }));
    setFiles((f) => [...f, ...picked]);
  };
  return (
    <div data-testid="switch-files">
      <button type="button" onClick={() => ref.current?.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`flex w-full flex-col items-center gap-1 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-colors ${drag ? 'border-[#0f766e] bg-[#ecfdf5]' : 'border-[#cbd5e1] bg-[#ffffff] hover:border-[#0f766e]'}`} data-testid="switch-file-drop">
        <FileUp size={20} className="text-[#0f766e]" />
        <span className="text-sm font-semibold text-[#0f172a]">{t.files_title}</span>
        <span className="text-xs text-[#64748b]">{t.files_hint}</span>
      </button>
      <input ref={ref} type="file" multiple accept={FILE_ACCEPT} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} data-testid="switch-file-input" />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f, i) => (
            <li key={f.id} className="flex items-center gap-2 rounded-xl bg-[#ffffff] px-3 py-2 text-sm" data-testid={`switch-file-${i}`}>
              <span className="min-w-0 flex-1 truncate text-[#0f172a]">{f.file.name}</span>
              {progress[f.id] === 'done' ? <span className="text-xs font-semibold text-[#0f766e]">{t.uploaded}</span>
                : progress[f.id] === 'error' ? <span className="text-xs font-semibold text-[#be123c]">{t.failed}</span>
                  : progress[f.id] === 'up' ? <Loader2 size={14} className="animate-spin text-[#0f766e]" /> : null}
              <select value={f.tag} disabled={busy} onChange={(e) => setFiles((fs) => fs.map((x) => (x.id === f.id ? { ...x, tag: e.target.value } : x)))}
                className="rounded-lg border border-[#cbd5e1] bg-[#ffffff] px-2 py-1 text-xs" data-testid={`switch-file-tag-${i}`}>
                {FILE_TAG_KEYS.map((k) => <option key={k} value={k}>{t.tags[k]}</option>)}
              </select>
              {!busy && <button type="button" onClick={() => setFiles((fs) => fs.filter((x) => x.id !== f.id))} aria-label="Remove file" className="text-[#94a3b8] hover:text-[#be123c]" data-testid={`switch-file-remove-${i}`}><X size={14} /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const uploadAll = async (files, url, setProgress) => {
  let failed = 0;
  for (const f of files) {
    setProgress((p) => ({ ...p, [f.id]: 'up' }));
    const body = new FormData();
    body.append('file', f.file);
    body.append('tag', f.tag);
    const ok = await fetch(url, { method: 'POST', body }).then((r) => r.ok).catch(() => false);
    if (!ok) failed += 1;
    setProgress((p) => ({ ...p, [f.id]: ok ? 'done' : 'error' }));
  }
  return failed;
};
